import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';
import type { PresentationItem } from '@/types/presentation';

// ── Types ─────────────────────────────────────────────────

export interface PresentationsPage {
  count: number;
  results: PresentationItem[];
}

type ContentType = 'presentation' | 'invitation' | 'render';

const PAGE_SIZE = 8;

// ── Keys ──────────────────────────────────────────────────

export const PRESENTATION_KEYS = {
  list: (type: ContentType, page: number) => [type + 's', page] as const,
  all: (type: ContentType) => [type + 's'] as const,
};

// ── Hooks ─────────────────────────────────────────────────

export function usePresentationsPage(page: number) {
  return useQuery({
    queryKey: PRESENTATION_KEYS.list('presentation', page),
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
        type: 'presentation',
      });
      const res = await api.get<PresentationsPage>(`${API_ROUTES.presentations}?${params}`);
      return res.data;
    },
    refetchInterval: (query) => {
      const hasGenerating = query.state.data?.results?.some((item) => item.status === 'generating');
      return hasGenerating ? 5_000 : 30_000;
    },
  });
}

export function useInvitationsPage(page: number) {
  return useQuery({
    queryKey: PRESENTATION_KEYS.list('invitation', page),
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
        type: 'invitation',
      });
      const res = await api.get<PresentationsPage>(`${API_ROUTES.presentations}?${params}`);
      return res.data;
    },
    refetchInterval: (query) => {
      const hasGenerating = query.state.data?.results?.some((item) => item.status === 'generating');
      return hasGenerating ? 5_000 : 30_000;
    },
  });
}

export function useRendersPage(page: number) {
  return useQuery({
    queryKey: PRESENTATION_KEYS.list('render', page),
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
        type: 'render',
      });
      const res = await api.get<PresentationsPage>(`${API_ROUTES.presentations}?${params}`);
      return res.data;
    },
    refetchInterval: (query) => {
      const hasGenerating = query.state.data?.results?.some((item) => item.status === 'generating');
      return hasGenerating ? 5_000 : 30_000;
    },
  });
}

// ── Refresh helper hook ───────────────────────────────────

export { PAGE_SIZE as CONTENT_LIST_PAGE_SIZE };
